/**
 * Italian — the source language.
 *
 * This is the one that gets written first and read by the client. `en.ts` is typed
 * against its shape, so adding a key here and forgetting the English is a build error.
 *
 * Wording rules that matter more than the words:
 *  - Statuses describe a fact, never a judgement. GPS is noisy and these lines are read
 *    by a manager about a named employee.
 *  - "Non disponibile" is its own state and never a softer way of saying "fuori area".
 *  - Destructive confirmations say what survives, not just what is lost.
 */

export const it = {
  common: {
    allEmployees: 'Tutti i dipendenti',
    typeToSearch: 'Scrivi per cercare…',
    noMatches: 'Nessun risultato',
    searchPeople: 'Cerca per nome, email o ruolo…',
    networkError: 'Impossibile raggiungere il server. Controlla la connessione e riprova.',
    databaseStarting: 'Il sistema si sta avviando. Riprova tra qualche secondo.',
    tryAgain: 'Riprova',
    waking: 'Avvio in corso, un momento…',
    charsLeftOne: 'Ancora {count} carattere.',
    charsLeftMany: 'Ancora {count} caratteri.',
    notAvailable: 'n/d',
    unknownError: 'Errore sconosciuto.',
    somethingWrong: 'Qualcosa è andato storto',
    backToList: 'Torna all’elenco',
    yes: 'Sì',
    no: 'No',
    apply: 'Applica',
    start: 'Inizio',
    end: 'Fine',
    reasonRequired: 'Motivo (obbligatorio)',
    save: 'Salva',
    cancel: 'Annulla',
    confirm: 'Conferma',
    areYouSure: 'Sei sicuro?',
    notice: 'Qualcosa non è andato',
    ok: 'OK',
    close: 'Chiudi',
    delete: 'Elimina',
    edit: 'Modifica',
    open: 'Apri',
    back: 'Indietro',
    all: 'Tutti',
    none: 'Nessuno',
    loading: 'Caricamento…',
    saving: 'Salvataggio…',
    search: 'Cerca',
    details: 'Dettagli',
    download: 'Scarica',
    from: 'Da',
    to: 'A',
    day: 'Giorno',
    today: 'Oggi',
    tomorrow: 'Domani',
    thisMonth: 'Questo mese',
    lastMonth: 'Mese scorso',
    thisWeek: 'Questa settimana',
    onlyToday: 'Solo oggi',
    employee: 'Dipendente',
    employees: 'Dipendenti',
    site: 'Cantiere',
    sites: 'Cantieri',
    job: 'Commessa',
    jobs: 'Commesse',
    company: 'Azienda',
    date: 'Data',
    time: 'Orario',
    status: 'Stato',
    notes: 'Note',
    notesOptional: 'Note (facoltative)',
    optional: 'facoltativo',
    required: 'obbligatorio',
    hours: 'Ore',
    error: 'Si è verificato un errore.',
    saveError: 'Errore nel salvataggio.',
    retry: 'Riprova',
    noResults: 'Nessun risultato.',
    noResultsHint: 'Prova a modificare la ricerca o i filtri.',
    clearFilters: 'Azzera filtri',
    select: 'Seleziona…',
    add: 'Aggiungi…',
    creating: 'Creazione…',
    description: 'Descrizione',
    priority: 'Priorità',
    normal: 'Normale',
    onlyOffice: 'Solo ufficio',
    allStaff: 'Tutto il personale',
    assignedEngineers: 'Tecnici assegnati',
    noJob: 'Senza commessa',
    outsideArea: 'Fuori area',
    daysLeft: '{count} gg',
    daysAgo: '{count} gg fa',
    address: 'Indirizzo',
    city: 'Città',
    latitude: 'Latitudine',
    longitude: 'Longitudine',
    postcode: 'CAP',
    accessNotes: 'Note di accesso',
    manage: 'Gestisci',
    assign: 'Assegna',
    remove: 'Rimuovi',
    field: 'Campo',
    newValue: 'Nuovo valore',
    reason: 'Motivo',
    reasonGiven: 'Motivo indicato',
    backToJobs: 'Torna alle commesse',
    backToShifts: 'Torna ai turni',
    shift: 'Turno',
    clockInTime: 'Ora di entrata',
    clockOutTime: 'Ora di uscita',
    noSiteLinked: 'Nessun cantiere associato.',
    noShiftsRecorded: 'Nessun turno registrato.',
    noDocumentsAttached: 'Nessun documento allegato.',
    noTeam: 'Nessun tecnico assegnato.',
    noAdjustments: 'Nessuna correzione registrata.',
    adjust: 'Modifica turno',
  },

  nav: {
    calendar: 'Calendario',
    leave: 'Ferie e permessi',
    more: 'Altro',
    openMenu: 'Apri il menu',
    closeMenu: 'Chiudi il menu',
    inventory: 'Inventario',
    vehicles: 'Veicoli',
    recycleBin: 'Cestino',
    messages: 'Messaggi',
    portal: 'Portale interno',
    dashboard: 'Dashboard',
    groupOperations: 'Operatività',
    groupRecords: 'Anagrafiche',
    groupCompliance: 'Conformità',
    groupSystem: 'Sistema',
    shifts: 'Turni',
    assignments: 'Assegnazioni',
    payroll: 'Ore e paghe',
    employees: 'Dipendenti',
    sites: 'Cantieri',
    jobs: 'Commesse',
    alerts: 'Scadenze',
    compliance: 'Documenti obbligatori',
    documents: 'Archivio documenti',
    audit: 'Registro attività',
    settings: 'Impostazioni',
  },

  theme: {
    label: 'Tema',
    system: 'Sistema',
    light: 'Chiaro',
    dark: 'Scuro',
  },

  locale: {
    label: 'Lingua',
    it: 'Italiano',
    en: 'English',
    ar: 'العربية',
  },

  table: {
    openRow: 'Apri',
    search: 'Cerca',
    searchPlaceholder: 'Cerca…',
    sortBy: 'Ordina per',
    ascending: 'crescente',
    descending: 'decrescente',
    view: 'Vista',
    viewTable: 'Tabella',
    viewCards: 'Schede',
    resultsOne: '{count} risultato',
    resultsMany: '{count} risultati',
    filteredFrom: 'di {total}',
  },

  dashboard: {
    title: 'Dashboard',
    onDutyNow: 'In servizio ora',
    jobsToday: 'Commesse oggi',
    toReview: 'Da verificare',
    toReviewHint: 'turni con segnalazioni, ultimi 30 giorni',
    unassigned: 'Senza assegnazione',
    unassignedHint: 'commesse programmate senza tecnico',
    nobodyOnDuty: 'Nessuno è attualmente in servizio.',
    shiftsToReview: 'Turni da verificare',
    noShiftsToReview: 'Nessun turno da verificare.',
    expiriesToHandle: 'Scadenze da gestire',
    noExpiries: 'Nessuna scadenza da gestire. Tutti i documenti sono in regola.',
    seeAll: 'Tutte',
    jobsWithoutEngineer: 'Commesse senza tecnico',
    allJobsAssigned: 'Tutte le commesse programmate hanno un tecnico assegnato.',
    allClear: 'Tutto in ordine',
    allClearHint: 'Nessuna attività richiede attenzione.',
  },

  shifts: {
    jobAndSite: 'Commessa · cantiere',
    noJob: 'Nessuna commessa',
    inPosition: 'Posizione all’entrata',
    outPosition: 'Posizione all’uscita',
    openInMap: 'Apri nella mappa',
    siteArea: 'Area del cantiere',
    needsReview: 'Da verificare',
    review: 'Segna come verificato',
    reviewTitle: 'Verifica il turno',
    reviewExplainer:
      'Conferma che il turno è corretto così com’è registrato. Le segnalazioni restano visibili — sono la prova di quello che è successo — ma il turno esce dalla coda ed entra nel conteggio delle ore.',
    reviewNote: 'Nota (facoltativa)',
    reviewNotePlaceholder: 'Es. confermato per telefono con il dipendente.',
    reviewedBy: 'Verificato da {name}, {date}',
    withdrawReview: 'Annulla verifica',
    reviewedBadge: 'Verificato',
    queueEmpty: 'Nessun turno da verificare.',
    onlyNeedsReview: 'Solo da verificare',
    breakShort: 'pausa {count} min',
    recordedDuration: 'Durata registrata',
    inProgressSuffix: '· in corso',
    deviceEventId: 'ID evento dispositivo',
    locationHint1:
      'La posizione viene rilevata solo al momento della timbratura, mai durante il turno.',
    locationHint2:
      'L’esito è calcolato dal server sulle coordinate grezze, non dal dispositivo.',
    adjustments: 'Modifiche',
    noAdjustmentsHint: 'Nessuna modifica. Il turno è come è stato registrato.',
    adjustmentSaved: 'Modifica salvata.',
    saveAdjustment: 'Salva modifica',
    breakMinutes: 'Pausa (minuti)',
    correctHint:
      'Ogni modifica viene registrata con il motivo e resa visibile al dipendente.',
    clockIn: 'Ora di entrata',
    clockOut: 'Ora di uscita',
    job: 'Commessa',
    locationData: 'Dati di posizione',
    inOutcome: 'Entrata · esito',
    inDistance: 'Entrata · distanza',
    outOutcome: 'Uscita · esito',
    outDistance: 'Uscita · distanza',
    noGps: 'GPS non disponibile',
    correct: 'Correggi il turno',
    reasonPlaceholder: 'Es. il dipendente ha confermato l’orario per telefono',
    title: 'Turni',
    subtitle: 'Timbrature, dati di posizione e correzioni',
    exportCsv: 'Esporta CSV',
    csvIn: 'Entrata',
    csvOut: 'Uscita',
    csvBreak: 'Pausa (min)',
    csvDistance: 'Distanza (m)',
    csvReason: 'Motivo',
    flaggedOnly: 'Solo con segnalazioni',
    searchPlaceholder: 'Dipendente, motivo, segnalazione…',
    includeUnreviewed: 'Includi non verificati nell’export',
    includeUnreviewedHint: 'Include turni chiusi automaticamente o contestati',
    countOne: '{count} turno',
    countMany: '{count} turni',
    reviewableTotal: 'Totale verificabile:',
    position: 'Posizione',
    duration: 'Durata',
    flags: 'Segnalazioni',
    onSite: 'In cantiere',
    gpsUnavailable: 'GPS n/d',
    excludedOne:
      '{count} turno è escluso dal totale e dall’export perché chiuso automaticamente o contestato. Va verificato prima di essere retribuito.',
    excludedMany:
      '{count} turni sono esclusi dal totale e dall’export perché chiusi automaticamente o contestati. Vanno verificati prima di essere retribuiti.',
  },

  assignments: {
    notesOptional: 'Note (facoltative)',
    title: 'Assegnazioni cantiere',
    subtitle:
      'Chi lavora dove. Un dipendente può timbrare solo nel cantiere a cui è assegnato quel giorno.',
    create: 'Nuova assegnazione',
    createTitle: 'Assegna una squadra a un cantiere',
    selectSite: 'Seleziona…',
    jobOptional: 'Commessa (facoltativa)',
    jobOptionalHint: 'Serve solo per attribuire le ore a una commessa.',
    noJob: 'Nessuna',
    startsOn: 'Dal',
    endsOn: 'Al',
    openEnded: 'Assegnazione di progetto (senza data di fine)',
    project: 'progetto',
    selectedCount: 'Dipendenti ({count} selezionati)',
    notesPlaceholder: 'Es. turno serale, accesso da via laterale',
    assignOne: 'Assegna {count} dipendente',
    assignMany: 'Assegna {count} dipendenti',
    assigning: 'Assegnazione…',
    createError: 'Errore nella creazione.',
    unassignedOne: '{count} dipendente senza cantiere il {date}',
    unassignedMany: '{count} dipendenti senza cantiere il {date}',
    unassignedHint: 'Senza assegnazione la timbratura viene registrata ma segnalata come anomalia.',
    noneForDay: 'Nessuna assegnazione per il {date}.',
    area: 'area {radius} m',
    noJobShort: 'senza commessa',
    remove: 'Rimuovi',
    removeConfirm:
      'Rimuovere l’assegnazione di {name} da {site}? I turni già timbrati restano invariati.',
  },

  payroll: {
    title: 'Ore e buste paga',
    subtitle: 'Ore per dipendente e per cantiere, esportabili per la contabilità',
    downloadPdf: 'Scarica PDF',
    downloadExcel: 'Scarica Excel',
    preparing: 'Preparazione…',
    groupBy: 'Raggruppa per',
    byWorker: 'Dipendente',
    bySite: 'Cantiere',
    includeUnreviewed: 'Includi turni non verificati',
    totalHours: 'Ore totali',
    decimalHoursHint: '{value} ore decimali',
    countedShifts: 'Turni conteggiati',
    excluded: 'Esclusi',
    toVerify: 'Da verificare',
    decimalHours: 'Ore decimali',
    noShifts: 'Nessun turno nel periodo selezionato.',
    exportFailed: 'Esportazione non riuscita.',
    detail: 'Dettaglio',
    hide: 'Nascondi',
    autoClosed: 'Auto-chiuso',
    disputed: 'Contestato',
    outsideArea: 'Fuori area',
    excludedOne:
      '{count} turno escluso dal totale e dall’esportazione perché chiuso automaticamente o contestato. Va verificato in Turni prima di essere retribuito.',
    excludedMany:
      '{count} turni esclusi dal totale e dall’esportazione perché chiusi automaticamente o contestati. Vanno verificati in Turni prima di essere retribuiti.',
  },

  personal: {
    heading: 'Dati personali',
    photo: 'Foto del tesserino',
    uploadPhoto: 'Carica foto',
    changePhoto: 'Cambia foto',
    removePhoto: 'Rimuovi',
    photoFailed: 'Caricamento della foto non riuscito.',
    edit: 'Modifica',
    save: 'Salva',
    cancel: 'Annulla',
    saveFailed: 'Salvataggio non riuscito.',
    firstName: 'Nome',
    lastName: 'Cognome',
    jobTitle: 'Mansione',
    phone: 'Telefono',
    dateOfBirth: 'Data di nascita',
    birthPlace: 'Luogo di nascita',
    taxCode: 'Codice fiscale',
    hiredOn: 'Data di assunzione',
    badgeNumber: 'Numero tesserino',
    address: 'Indirizzo',
    emergencyContact: 'Contatto di emergenza',
  },

  employees: {
    assignmentRange: 'dal {from} al {to}',
    assignmentOpen: 'dal {from} — a progetto',
    hoursRecent: 'Ore (ultimi 30 turni)',
    title: 'Dipendenti',
    subtitle: 'Gli account dell’app si creano qui — non esiste registrazione autonoma',
    create: 'Nuovo dipendente',
    createHint:
      'Con l’email riceverà un invito con una password temporanea. Senza email viene solo registrato: l’account lo crei più tardi.',
    addOnly: 'Aggiungi dipendente',
    createAccount: 'Crea account',
    firstName: 'Nome',
    lastName: 'Cognome',
    phone: 'Telefono',
    searchPlaceholder: 'Nome, email, ruolo…',
    countOne: '{count} dipendente',
    countMany: '{count} dipendenti',
    role: 'Ruolo',
    createdAt: 'Creato',
    email: 'Email',
    name: 'Nome',
    suspend: 'Sospendi',
    reactivate: 'Riattiva',
    backToList: 'Torna all’elenco',
    hoursLast30: 'Ore (ultimi 30 turni)',
    shiftsRecorded: 'Turni registrati',
    withFlags: 'Con segnalazioni',
    documentsToHandle: 'Documenti da gestire',
    ofExpected: 'su {total} previsti',
    recentShifts: 'Ultimi turni',
    noShifts: 'Nessun turno registrato.',
    activeAssignments: 'Assegnazioni attive',
    noAssignments: 'Nessun cantiere assegnato.',
    assign: 'Assegna',
    documentFile: 'Fascicolo documenti',
    manage: 'Gestisci',
    fromDate: 'dal {date}',
    toDate: 'al {date}',
    onProject: '— a progetto',
    privacy: 'Privacy',
    locationNotice: 'Informativa posizione:',
    noticeRead: 'letta il {date}',
    noticeUnread: 'non ancora letta',
    noticeExplainer:
      'Conferma di presa visione, non consenso: il trattamento si basa sul contratto di lavoro e sul legittimo interesse.',
    onFile: 'agli atti',
    notOnFile: 'non agli atti',
    expiresOn: 'scade il {date}',
  },

  sites: {
    clientHint: 'Per chi è il lavoro. Le commesse su questo cantiere lo ereditano.',
    noClient: '— Nessun cliente —',
    newClient: '+ Nuovo cliente…',
    newClientPlaceholder: 'Nome del cliente',
    deleteTitle: 'Eliminare il cantiere?',
    deleteConfirm:
      '“{name}” finisce nel cestino e può essere ripristinato per 30 giorni. Lo storico dei turni resta intatto.',
    tolerances: 'Tolleranze di timbratura',
    tolerancesHint:
      'Lascia vuoto per usare le impostazioni generali. Compilali solo per i cantieri molto grandi — aeroporti, data center, poli logistici — dove il segnaposto non può essere vicino a ogni postazione.',
    edgeTolerance: 'Margine oltre il confine (m)',
    edgeToleranceHint:
      'Metri di tolleranza oltre l’area prima di segnalare una timbratura. Diverso dall’allargare il raggio: il raggio dice quanto è grande il cantiere, questo quanto perdonare al suo bordo.',
    trustOverride: 'Precisione affidabile (m)',
    rejectOverride: 'Precisione inutilizzabile (m)',
    overrideEmpty: 'Generale',
    allowOutsideOverride: 'Timbratura fuori area',
    useGlobal: 'Come impostazioni generali',
    forceAllow: 'Sempre consentita (con motivo)',
    forceDeny: 'Mai consentita',
    largeSiteWarning:
      'Cantiere grande: con un raggio oltre 500 m conviene disegnare l’area reale invece di allargare il cerchio, altrimenti la recinzione include anche i vicini.',
    collapse: 'Riduci la mappa',
    dropPin: 'Posiziona il segnaposto',
    dropPinActive: 'Clicca sulla mappa per posizionare il segnaposto.',
    movePinHint: 'Trascina il segnaposto per spostarlo.',
    mapModifier: 'Tieni premuto Maiusc per spostare o ingrandire la mappa.',
    expand: 'Ingrandisci la mappa',
    pinOutside: 'Il segnaposto deve trovarsi dentro l’area: spostalo o ridisegna.',
    pinLocked: 'Il segnaposto non può uscire dall’area disegnata.',
    done: 'Fatto',
    drawBoundary: 'Disegna l’area',
    editBoundary: 'Modifica l’area',
    clearBoundary: 'Rimuovi l’area',
    finishBoundary: 'Salva l’area',
    undoPoint: 'Annulla punto',
    boundaryActive: 'Area disegnata con {count} punti: sostituisce il raggio.',
    radiusActive: 'Nessuna area disegnata: vale il raggio di {count} m.',
    pointsPlaced: '{count} punti. Clicca per aggiungerne altri.',
    drawHint: 'Clicca sulla mappa per delimitare l’area. Servono ancora {count} punti.',
    noCoordinates: 'Senza coordinate: il segnaposto non si sposta.',
    addressSearch: 'Cerca l’indirizzo',
    addressSearchHint:
      'Scrivi e scegli dall’elenco: compila da solo città, CAP e coordinate.',
    addressPlaceholder: 'Es. Via Roma 42, Milano',
    mapLabel: 'Posizione e area di timbratura',
    namePlaceholder: 'Es. Residenza Aurora — cabina elettrica',
    radiusLabel: 'Raggio area (m)',
    newSite: 'Nuovo cantiere',
    radiusHint: 'Tra 25 e 5000. Un appartamento e un capannone non hanno la stessa area.',
    accessNotes: 'Note di accesso',
    accessNotesHint:
      'Codici cancello, parcheggio, a chi rivolgersi. È il campo più utile dell’app in cantiere.',
    title: 'Cantieri',
    subtitle: 'Coordinate e area di timbratura',
    create: 'Nuovo cantiere',
    edit: 'Modifica cantiere',
    geofenceWarning:
      'Le coordinate e il raggio determinano l’esito di ogni timbratura futura su questo cantiere. Ogni modifica viene registrata nel registro attività.',
    client: 'Cliente',
    address: 'Indirizzo',
    coordinates: 'Coordinate',
    area: 'Area',
    missing: 'Mancanti',
    radius: 'Raggio geofence (m)',
    searchPlaceholder: 'Nome, cliente, indirizzo…',
    countOne: '{count} cantiere',
    countMany: '{count} cantieri',
  },

  jobs: {
    assignTo: 'Assegna a',
    assignToHint: 'Il lavoro compare nella loro app e ricevono una notifica.',
    saveAsDraft: 'Salva come bozza',
    saveAsDraftHint: 'Una bozza non è ancora confermata: si vede nell’app ma non ci si può timbrare.',
    siteArea: 'Area cantiere: {radius} m',
    attachments: 'Allegati',
    attachmentsHint:
      'Disegni, contratti, valutazioni rischi. I tecnici assegnati li vedono nell\u2019app.',
    attachedCount: '{count} file da allegare',
    workingHours: 'Orari di timbratura',
    workingHoursHint:
      'Valgono solo se la commessa ha un orario previsto. Lascia \u201ccome impostazioni generali\u201d salvo motivi specifici.',
    earlyLabel: 'Entrata prima dell\u2019orario',
    lateLabel: 'Uscita dopo l\u2019orario',
    useGlobal: 'Come impostazioni generali',
    allowIt: 'Consentita',
    blockIt: 'Bloccata',
    noScheduleNote:
      'Senza un orario previsto queste regole non si applicano: non c\u2019\u00e8 un \u201cprima\u201d o un \u201cdopo\u201d.',
    noTeamWarning: 'Nessun tecnico assegnato — la commessa non compare in nessuna app.',
    signatureRequired: 'Firma richiesta',
    changeStatus: 'Cambia stato',
    finalState: 'Questa commessa è in uno stato finale e non può essere modificata.',
    description: 'Descrizione',
    access: 'Accesso',
    siteNotes: 'Note dal cantiere',
    scheduling: 'Programmazione',
    statusChangeFailed: 'Errore nel cambio di stato.',
    descriptionPlaceholder: 'Es. Sostituzione quadro elettrico principale',
    title: 'Commesse',
    subtitle: 'Lavori programmati, in corso e chiusi',
    reference: 'Riferimento',
    jobTitle: 'Titolo',
    urgent: 'Urgente',
    scheduledStart: 'Inizio previsto',
    scheduledEnd: 'Fine prevista',
    createJob: 'Crea commessa',
    shiftsOnJob: 'Turni su questa commessa',
    searchPlaceholder: 'Riferimento, titolo, cliente…',
    countOne: '{count} commessa',
    countMany: '{count} commesse',
    client: 'Cliente',
    priority: 'Priorità',
    scheduled: 'Programmato',
    team: 'Squadra',
  },

  documents: {
    changeVisibility: 'Visibilità',
    changeVisibilityTitle: 'Chi può vedere “{name}”',
    changeVisibilityHint:
      'Restringere la visibilità ha effetto immediato. Un documento già aperto da qualcuno resta aperto finché non ricarica.',
    customCategory: 'Etichetta personalizzata',
    customCategoryHint:
      'Come la chiami tu. Compare nell’elenco al posto di “Altro” ed è ricercabile.',
    customCategoryPlaceholder: 'Es. Polizza assicurativa',
    visibilityHint: 'Chi può vedere questo documento nell’app.',
    uploadDone: 'File caricato.',
    dropHere: 'Trascina qui un file, oppure clicca per sceglierlo',
    accepted: 'PDF, P7M, JPG, PNG, HEIC, Word, Excel, OpenDocument · max 50 MB (immagini 25 MB)',
    uploading: 'Caricamento in corso…',
    uploadFailed: 'Caricamento non riuscito.',
    typeRejected: 'Formato non accettato. Usa PDF, P7M, JPG, PNG, HEIC, Word, Excel o OpenDocument.',
    tooLarge: 'File troppo grande: il limite è {size}.',
    uploadFor: 'Carica un file per “{name}”',
    addDocument: 'Carica documento',
    chooseCategory: 'Categoria',
    title: 'Documenti',
    subtitle: 'Contratti, disegni, certificati e materiale dal cantiere',
    searchPlaceholder: 'Nome file',
    category: 'Categoria',
    visibility: 'Visibilità',
    size: 'Dimensione',
    uploaded: 'Caricato',
    file: 'File',
    countOne: '{count} documento',
    countMany: '{count} documenti',
    deleteConfirm: 'Eliminare definitivamente {name}? L’operazione resta nel registro attività.',
  },

  folders: {
    addFile: 'Aggiungi file (es. retro)',
    addFileTitle: 'Aggiungi un file a {label}',
    addFileHint: 'Si unisce al documento attuale, con le stesse date: il retro della carta d\'identità, una seconda pagina.',
    dropSides: 'Trascina qui il file, oppure fronte e retro insieme, o clicca per sceglierli',
    addAnotherSide: 'Aggiungi un altro file a questo documento (es. il retro)',
    sidesTogether: 'Questi {count} file saranno un solo documento (es. fronte e retro).',
    fileCount: '{count} file',
    showFiles: 'Mostra tutti i file ({count})',
    hideFiles: 'Nascondi file',
    foldersHintVehicle: 'Fatture di manutenzione, multe, sinistri e altro. Clicca una cartella per aprirla.',
    newFolderHintVehicle: 'La cartella compare per ogni veicolo.',
    newDocumentHintVehicle:
      'Si aggiunge all\'elenco di ogni veicolo, ad es. "Telepass" o "Carta carburante". Riceverai un avviso prima della scadenza.',
    vehiclePlaceholder: 'Es. Carta carburante',
    mandatoryVehicleHint: 'Risulta mancante per ogni veicolo in uso che non ce l\'ha.',
    correct: 'Correggi date e dati',
    correctTitle: 'Correggi — {name}',
    correctHint: 'Per correggere un errore. Se il documento è stato davvero rinnovato, usa Rinnova.',
    replaceFile: 'Sostituisci file',
    attachFile: 'Allega file',
    shortCode: 'Codice nel nome del file',
    shortCodeHint: 'I file caricati vengono rinominati in automatico, es. {example}. Lascia vuoto per usare il nome.',
    autoNameHint: 'Il file viene rinominato in automatico con documento, nome e data di scadenza.',
    replaceTitle: 'Sostituisci file — {name}',
    attachTitle: 'Allega file — {name}',
    replaceHint:
      'Il documento resta lo stesso, con date e storico; cambia solo il file. Il file precedente resta nel registro attività.',
    editItem: 'Rinomina o modifica voce',
    removeItem: 'Togli dall’elenco',
    removedTitle: 'Voci tolte dall’elenco ({count}) — mostra',
    restore: 'Ripristina',
    documentsTitle: 'Documenti',
    documentsHint: 'Una copia valida per ciascuno. Ricaricandolo si rinnova; quello vecchio viene archiviato.',
    summary: '{present} di {required} documenti obbligatori presenti',
    expiredCount: '{count} scaduti',
    expiringCount: '{count} in scadenza',
    noDocuments: 'Ancora nessun documento in elenco.',
    foldersTitle: 'Cartelle',
    foldersHint: 'Tutti i file che servono: corsi, nomine, qualsiasi altra cosa. Clicca una cartella per aprirla.',
    foldersHintCompany: 'Tutti i file che servono: polizze, contratti, verbali. Clicca una cartella per aprirla.',
    noFolders: 'Ancora nessuna cartella.',
    newDocument: 'Nuovo documento',
    addCompanyDocument: 'Aggiungi documento aziendale',
    newFolder: 'Nuova cartella',
    newSubfolder: 'Nuova sottocartella',
    upload: 'Carica',
    more: 'Altre azioni',
    edit: 'Modifica',
    rename: 'Rinomina',
    history: 'Mostra versioni precedenti',
    hideHistory: 'Nascondi versioni precedenti',
    current: 'attuale',
    notOnFile: 'Non presente',
    noScan: 'Registrato senza file',
    emptyShort: 'Vuota',
    emptyFolder: 'Questa cartella è vuota.',
    countOne: '{count} file',
    countMany: '{count} file',
    subCountOne: '{count} sottocartella',
    subCountMany: '{count} sottocartelle',
    dropInto: 'Trascina qui i file da aggiungere a “{name}”, oppure clicca per sceglierli',
    deleteFolderConfirm: 'Eliminare la cartella “{name}”? Si può eliminare solo una cartella vuota.',
    deleteDocumentConfirm:
      'Togliere “{name}” dall’elenco? Possibile solo se non c’è nulla archiviato. Si potrà ripristinare.',
    uploadTitle: 'Carica — {label}',
    multipleExplainer: 'Si aggiunge alla cartella; quanto già presente non viene sostituito.',
    renewExplainer: 'La copia attuale viene archiviata, non sovrascritta.',
    dropMany: 'Trascina qui i file, oppure clicca per sceglierli — anche più di uno',
    dropOne: 'Trascina qui il file, oppure clicca per sceglierlo',
    addMore: 'Aggiungi altri file',
    uploadN: 'Carica {count} file',
    uploadingProgress: 'Caricamento {done} di {total}…',
    fileFailed: '“{name}” non è stato caricato: {reason}',
    noFileHint: 'Non hai ancora il file? Salva ora le date e allega la scansione più tardi.',
    notes: 'Note',
    editTitle: 'Modifica — {name}',
    subfolderTitle: 'Nuova sottocartella in “{name}”',
    newFolderHintWorker: 'La cartella compare nel fascicolo di ogni dipendente.',
    newFolderHintCompany: 'Uno spazio per qualsiasi documento aziendale.',
    newDocumentHintWorker:
      'Si aggiunge all’elenco di ogni dipendente, ad es. “Patente C”. Riceverai un avviso prima della scadenza.',
    newDocumentHintCompany:
      'Dagli un nome, poi allega il file e la scadenza. Riceverai un avviso prima che scada.',
    createAndUpload: 'Crea e carica',
    createFolder: 'Crea cartella',
    name: 'Nome',
    description: 'Descrizione',
    folderPlaceholder: 'Es. Nomine',
    workerPlaceholder: 'Es. Patente C',
    companyPlaceholder: 'Es. Certificato prevenzione incendi',
    tracksExpiry: 'Ha una scadenza',
    tracksExpiryHint: 'La data viene chiesta al caricamento e arriva un avviso prima della scadenza.',
    warnDays: 'Avvisami con questi giorni di anticipo',
    mandatory: 'Obbligatorio',
    mandatoryWorkerHint: 'Risulta mancante per ogni dipendente che non ce l’ha.',
    mandatoryCompanyHint: 'Risulta mancante finché non viene archiviato.',
  },

  viewer: {
    preview: 'Anteprima',
    openTab: 'Apri in una nuova scheda',
    previous: 'Precedente',
    next: 'Successivo',
    noPreview: 'Anteprima non disponibile per questo formato',
    noPreviewHint: 'Word, Excel, foto HEIC e file firmati .p7m si aprono con il loro programma. Scaricalo per vederlo.',
    failed: 'Impossibile caricare l’anteprima.',
  },

  send: {
    button: 'Invia documenti',
    buttonCount: 'Invia documenti ({count})',
    select: 'Seleziona {name}',
    selectAll: 'Seleziona tutti quelli mostrati',
    clearSelection: 'Annulla selezione',
    title: 'Invia documenti',
    hint: 'Scegli le persone e i documenti, poi inviali al cliente via email oppure scarica uno ZIP.',
    who: 'Chi',
    selectedCount: '{count} selezionati',
    which: 'Quali documenti',
    employeeDocuments: 'Documenti dei dipendenti',
    companyDocuments: 'Documenti aziendali',
    all: 'Seleziona tutti',
    none: 'Nessuno',
    summaryOne: '1 documento · {size}',
    summary: '{count} documenti · {size}',
    showFiles: 'Mostra l’elenco',
    missing: '{count} non presenti:',
    tooBig: 'Troppo grande per una sola email. Dividi in due invii, oppure scarica lo ZIP.',
    toWhom: 'A chi',
    recipients: 'Email del cliente',
    recipientsHint: 'Fino a 5 indirizzi, separati da virgola.',
    subject: 'Oggetto',
    message: 'Messaggio',
    defaultSubject: 'Documentazione personale - HH Electrical Systems',
    defaultMessage:
      'Buongiorno,\nin allegato la documentazione richiesta per l’accesso in cantiere.\nCordiali saluti',
    copyToMe: 'Inviami una copia',
    privacy:
      'Questi documenti contengono dati personali: invia solo ciò che il cliente ha chiesto. Ogni documento inviato resta nel registro attività.',
    emailPending:
      'L’invio email dal portale si attiva appena AWS approva il servizio di posta. Nel frattempo scarica lo ZIP e allegalo a un’email dalla tua casella.',
    sendEmail: 'Invia email',
    sending: 'Invio in corso…',
    downloadZip: 'Scarica ZIP',
    preparing: 'Preparazione…',
    zipReady: 'ZIP pronto: {count} documenti, {size}. Il download è partito.',
    sent: 'Inviati {count} documenti a {to}.',
  },

  vehicles: {
    drivingTitle: 'Veicolo aziendale',
    drivingNone: 'Nessun veicolo in uso.',
    title: 'Veicoli',
    subtitle: 'I mezzi aziendali: chi li usa e se i documenti sono in regola',
    add: 'Aggiungi veicolo',
    addHint: 'Serve solo la targa. Il resto si può completare dopo.',
    editTitle: 'Modifica {plate}',
    searchPlaceholder: 'Targa, modello, conducente…',
    countOne: '{count} veicolo',
    countMany: '{count} veicoli',
    empty: 'Ancora nessun veicolo.',
    vehicle: 'Veicolo',
    plate: 'Targa',
    name: 'Soprannome',
    nameHint: 'Come lo chiama l\'ufficio, ad es. "Furgone bianco".',
    make: 'Marca',
    model: 'Modello',
    year: 'Anno',
    fuel: 'Alimentazione',
    registeredOn: 'Prima immatricolazione',
    km: 'Km',
    kmNow: 'Km attuali',
    kmHint: 'Dal cruscotto. Facoltativo, ma serve per tagliandi e carta carburante.',
    vin: 'Numero di telaio',
    notes: 'Note',
    holder: 'Conducente',
    driver: 'Conducente',
    nobody: 'Nessuno',
    since: 'dal {date}',
    dueBack: 'da restituire il {date}',
    dueBackOn: 'Da restituire il',
    inUse: 'In uso',
    documents: 'Documenti',
    documentsTitle: 'Documenti del veicolo',
    nextExpiry: 'Prossima scadenza',
    nextExpiryOn: 'prossima scadenza {date}',
    docsOk: 'In regola',
    docsExpired: '{count} scaduti',
    docsExpiring: '{count} in scadenza',
    docsMissing: '{count} mancanti',
    details: 'Dettagli',
    historyTitle: 'Chi lo ha usato',
    noHistory: 'Nessuno lo ha ancora usato.',
    from: 'Dal',
    to: 'Al',
    assign: 'Assegna',
    handOver: 'Passa a un altro',
    return: 'Restituisci',
    assignTitle: 'Assegna {plate}',
    handOverTitle: 'Passa {plate} da {name} a un altro',
    handOverHint: 'Chiude l\'uso del conducente attuale e apre quello nuovo in un solo passaggio.',
    returnTitle: 'Restituzione di {plate} da {name}',
    toGarage: 'Va direttamente in officina (manutenzione)',
    assignNotesPlaceholder: 'Es. cantiere Milano, con carta carburante',
    returnNotesPlaceholder: 'Es. graffio sul paraurti posteriore',
    deleteConfirm: 'Eliminare {plate}? Lo storico e i documenti restano.',
    status: {
      available: 'Disponibile',
      assigned: 'In uso',
      maintenance: 'In manutenzione',
      retired: 'Dismesso',
    },
    fuels: {
      petrol: 'Benzina',
      diesel: 'Diesel',
      electric: 'Elettrica',
      hybrid: 'Ibrida',
      lpg: 'GPL',
      methane: 'Metano',
    },
  },

  rename: {
    title: 'Rinomina file',
    name: 'Nuovo nome',
    extensionKept: 'L\'estensione {ext} resta, così il file si apre ancora con il programma giusto.',
    file: 'Rinomina file',
    short: 'Rinomina',
  },

  exempt: {
    notRequiredPerson: 'Non richiesto per questa persona',
    notRequiredVehicle: 'Non richiesto per questo veicolo',
    requiredAgain: 'Di nuovo obbligatorio',
    confirm:
      '"{name}" non risulterà più mancante e non genererà avvisi. Puoi renderlo di nuovo obbligatorio in qualsiasi momento. Continuare?',
  },

  sheet: {
    export: 'Esporta Excel',
    exporting: 'Preparazione…',
    import: 'Importa Excel',
    importTitle: 'Importa dipendenti da Excel',
    importSitesTitle: 'Importa cantieri da Excel',
    importVehiclesTitle: 'Importa veicoli da Excel',
    newSite: 'nuovo cantiere',
    newVehicle: 'nuovo veicolo',
    importCompanyTitle: 'Importa documenti aziendali da Excel',
    newDocument: 'nuovo documento',
    importHint:
      'Usa il file di "Esporta Excel". Vedrai tutte le modifiche prima che vengano salvate. Le celle vuote lasciano il dato com\'è; "-" lo cancella.',
    drop: 'Trascina qui il file Excel modificato, oppure clicca per sceglierlo',
    checking: 'Lettura del file…',
    updates: '{count} da aggiornare',
    creates: '{count} nuovi',
    unchanged: '{count} invariati',
    errors: '{count} con errori (esclusi)',
    done: 'Salvati: {count} dipendenti',
    apply: 'Applica {count} modifiche',
    another: 'Scegli un altro file',
    row: 'riga {row}',
    new: 'nuovo dipendente',
    skipped: 'verrà esclusa',
    changed: '{count} modifiche',
    empty: '(vuoto)',
    nothing: 'Niente da cambiare: il file corrisponde ai dati del portale.',
  },

  bulk: {
    button: 'Carica cartelle',
    title: 'Carica cartelle',
    hint: 'Trascina una cartella per persona, con il suo nome (es. "Mario Rossi"). Ogni file viene abbinato al suo documento dal nome. Controlli tutto prima che venga caricato.',
    drop: 'Trascina qui le cartelle',
    dropMore: 'Trascina qui altre cartelle',
    dropExample: 'es. una cartella "Mario Rossi" con "carta identità.pdf", "codice fiscale.pdf", "contratto.pdf"',
    pick: 'Scegli una cartella',
    pickNote: 'Chrome chiede conferma quando scegli una cartella; trascinandola no.',
    pickFiles: 'Scegli singoli file',
    photoNeedsImage: 'Foto (solo immagini JPG o PNG)',
    looseFiles: 'File senza cartella',
    choosePerson: 'Scegli la persona',
    person: 'Persona per la cartella {folder}',
    noMatch: 'Non trovata: scegli a chi appartiene',
    matched: 'Trovata dal nome della cartella',
    chosen: 'Scelta a mano',
    removeFolder: 'Togli',
    replaces: 'sostituisce quello attuale (scade il {date})',
    replacesUndated: 'sostituisce quello attuale',
    typeFor: 'Documento per {name}',
    skip: '- Non caricare -',
    expiryFor: 'Data di scadenza per {name}',
    expiry: 'Data di scadenza',
    photo: 'Foto (profilo e tesserino)',
    sameDates: 'Stesse date del primo file',
    upload: 'Carica {count} file',
    uploading: 'Caricamento {done} di {total}…',
    needPerson: '{count} cartelle senza persona',
    needDates: '{count} date di scadenza mancanti',
    done: '{count} file caricati',
    failedCount: '{count} non riusciti: leggi il motivo e riprova',
    notAccepted: 'Questo tipo di file non è accettato.',
    tooBig: 'Troppo grande ({size}).',
  },

  badge: {
    title: 'Tesserino di riconoscimento',
    needs: 'Per generarlo aggiungi: {fields}.',
    ready: 'Pronto da stampare, con la foto del profilo.',
    readyNoPhoto: 'Pronto: scegli la foto quando lo generi.',
    generate: 'Genera tesserino',
    dialogTitle: 'Tesserino di riconoscimento - {name}',
    hint: 'Controlla la foto, poi genera il tesserino: un PDF da stampare, ritagliare e plastificare.',
    photoProfile: 'La foto del profilo',
    photoFound: 'Trovata tra i documenti: {name}',
    photoChosen: 'La foto che hai scelto',
    noPhoto: 'Nessuna foto trovata: scegline una dal computer.',
    unusable: 'La foto del profilo è in un formato che il tesserino non può usare: scegline un’altra.',
    choose: 'Scegli un’altra foto',
    done: 'Fatto: genera il tesserino',
    generating: 'Generazione…',
    downloaded: 'Tesserino generato: {name}',
    missing: 'Manca ancora: {fields}. Completa prima i dati personali.',
  },

  renameAll: {
    button: 'Rinomina tutti i file',
    title: 'Rinomina tutti i file',
    hint: 'Dà a tutti i file già caricati il nome automatico: codice, nome, data di scadenza. I nuovi caricamenti hanno già questo nome. I vecchi nomi restano nel registro attività.',
    count: 'Verranno rinominati {count} file. Per esempio:',
    more: 'e altri {count}',
    apply: 'Rinomina {count} file',
    nothing: 'Tutti i file hanno già il nome automatico.',
    done: 'Fatto: {count} file rinominati.',
  },

  compliance: {
    title: 'Documenti e scadenze',
    subtitle: 'Documenti dei dipendenti e dell’azienda, con le scadenze da rinnovare',
    missingDocuments: 'Documenti mancanti',
    expired: 'Scaduti',
    expiring: 'In scadenza',
    companyDocuments: 'Documenti aziendali',
    toHandle: '{count} da gestire',
    toHandleShort: 'Da gestire',
    completeness: 'Completezza',
    missing: 'Mancanti',
    openFile: 'Apri fascicolo',
    closeFile: 'Chiudi',
    fileFor: 'Fascicolo — {name}',
    fileSummary: '{missing} mancanti · {expired} scaduti · {expiring} in scadenza',
    document: 'Documento',
    reference: 'Riferimento',
    issued: 'Rilascio',
    expiry: 'Scadenza',
    register: 'Registra',
    renew: 'Rinnova',
    recordTitle: 'Registra — {label}',
    renewTitle: 'Rinnova — {label}',
    recordExplainer:
      'Il documento precedente viene archiviato, non sovrascritto: "cosa avevamo agli atti il 12 marzo" è una domanda che prima o poi arriva.',
    filename: 'Nome file',
    referenceNumber: 'Numero / protocollo',
    issuedAt: 'Data di rilascio',
    expiryRequired: 'Obbligatoria per questo documento.',
    mandatory: 'Obbligatorio',
  },

  alerts: {
    title: 'Scadenze e segnalazioni',
    subtitle: 'Generate automaticamente ogni notte da documenti e certificazioni',
    rescan: 'Ricontrolla ora',
    rescanning: 'Controllo…',
    critical: 'Critiche',
    criticalHint: 'scadute o entro 14 giorni',
    warning: 'Attenzione',
    warningHint: 'entro 30 giorni o mancanti',
    info: 'Informative',
    showAcknowledged: 'Mostra anche quelle già prese in carico',
    countOne: '{count} segnalazione',
    countMany: '{count} segnalazioni',
    severity: 'Gravità',
    searchPlaceholder: 'Dipendente, documento…',
    alert: 'Segnalazione',
    concerns: 'Riguarda',
    kind: 'Tipo',
    dueOn: 'Scadenza',
    detectedAt: 'Rilevata',
    acknowledge: 'Prendi in carico',
    acknowledged: 'preso in carico',
    acknowledgedBy: 'Preso in carico da {name} — {note}',
    acknowledgeExplainer:
      'La segnalazione resta aperta finché il documento non viene rinnovato, ma smette di comparire tra quelle da gestire.',
    notePlaceholder: 'Es. rinnovo richiesto il 21/09, previsto entro giovedì',
    noneOpen: 'Nessuna scadenza aperta. Tutti i documenti sono in regola.',
    noneToHandle: 'Nessuna scadenza da gestire.',
  },

  calendar: {
    title: 'Calendario',
    subtitle: 'Appuntamenti, scadenze e assenze in un solo posto.',
    today: 'Oggi',
    previous: 'Mese precedente',
    next: 'Mese successivo',
    add: 'Nuovo appuntamento',
    addForDay: 'Aggiungi',
    allDay: 'Tutto il giorno',
    until: 'fino al {date}',
    nothing: 'Niente in programma in questo giorno.',
    more: '+{count}',
    itemsOne: '{count} voce',
    itemsMany: '{count} voci',
    kind: {
      appointment: 'Appuntamento',
      deadline: 'Scadenza',
      leave: 'Assenza',
    },
    source: {
      document: 'Documento',
      job: 'Fine commessa',
      equipment: 'Attrezzatura da restituire',
      vehicle: 'Veicolo da restituire',
      approved: 'Approvata',
      pending: 'In attesa di approvazione',
    },
    attendees: 'Partecipanti',
    everyone: 'Visibile a tutti',
    open: 'Apri',
    editTitle: 'Modifica appuntamento',
    newTitle: 'Nuovo appuntamento',
    fieldTitle: 'Titolo',
    place: 'Luogo',
    startDate: 'Inizio',
    endDate: 'Fine (facoltativa)',
    startTime: 'Ora di inizio',
    endTime: 'Ora di fine (facoltativa)',
    site: 'Cantiere',
    job: 'Commessa',
    noSite: 'Nessun cantiere',
    noJob: 'Nessuna commessa',
    attendeesField: 'Chi partecipa',
    attendeesHint: "Se non scegli nessuno, l'appuntamento è visibile a tutti.",
    attendeesSelf: "Lo vedi solo tu e l'ufficio.",
    endBeforeStart: "La fine precede l'inizio.",
    deleteConfirm: "Eliminare l'appuntamento {title}?",
    deleteConfirmLabel: 'Elimina appuntamento',
    legend: 'Legenda',
  },

  leave: {
    title: 'Ferie e permessi',
    subtitle: "Le richieste dei dipendenti: l'ufficio le approva o le rifiuta.",
    tabs: {
      pending: 'Da gestire',
      approved: 'Approvate',
      rejected: 'Rifiutate',
      all: 'Tutte',
    },
    status: {
      pending: 'In attesa',
      approved: 'Approvata',
      rejected: 'Rifiutata',
      cancelled: 'Annullata',
    },
    person: 'Dipendente',
    type: 'Tipo',
    period: 'Periodo',
    note: 'Note del dipendente',
    decision: 'Decisione',
    requested: 'Richiesta il',
    hoursRange: 'dalle {from} alle {to}',
    approve: 'Approva',
    reject: 'Rifiuta',
    searchPlaceholder: 'Dipendente, tipo, note…',
    countOne: '{count} richiesta',
    countMany: '{count} richieste',
    empty: 'Nessuna richiesta in questa vista.',
    cannotDecide: "Non puoi approvare o rifiutare: per impostazione le richieste le decide solo l'amministratore.",
    officeOnly: "Questa pagina è per l'ufficio. Le richieste di ferie e permessi si inviano dall'app.",
    disabled: "Le richieste dall'app sono disattivate nelle Impostazioni. Quelle già inviate restano qui.",
    decidedBy: 'da {name}',
    approveTitle: 'Approva la richiesta',
    rejectTitle: 'Rifiuta la richiesta',
    dialogHint: 'Il dipendente riceve una notifica con la tua risposta.',
    noteLabel: 'Nota per il dipendente (facoltativa)',
    notePlaceholder: 'Es. confermato, oppure: troppi colleghi assenti in quei giorni',
  },

  audit: {
    adminOnly: 'Solo amministratori',
    adminOnlyHint:
      'Il registro attività registra quello che fa tutto l’ufficio, responsabili compresi, quindi può leggerlo solo un amministratore: è questo che lo rende una prova. Chiedi a chi ha l’utenza di amministratore.',
    title: 'Registro attività',
    subtitle: 'Append-only. Nulla può essere cancellato da qui.',
    actor: 'Utente',
    action: 'Azione',
    entity: 'Oggetto',
    when: 'Quando',
    immutableNotice:
      'Questo registro non può essere modificato né cancellato. È la prova che protegge sia l’azienda sia il dipendente quando un turno viene contestato.',
    system: 'Sistema',
    searchPlaceholder: 'Utente, azione, oggetto…',
    countOne: '{count} voce',
    countMany: '{count} voci',
    actions: {
      user_created: 'Utente creato',
      user_updated: 'Utente modificato',
      user_exported: 'Dipendenti esportati in Excel',
      user_reinvited: 'Invito inviato di nuovo',
      user_login_created: 'Accesso con nome creato',
      user_password_reset: 'Password reimpostata',
      user_suspended: 'Utente sospeso',
      user_reactivated: 'Utente riattivato',
      shift_clock_in: 'Entrata',
      shift_clock_out: 'Uscita',
      shift_adjusted: 'Turno modificato',
      shift_auto_closed: 'Turno chiuso automaticamente',
      job_created: 'Commessa creata',
      job_status_changed: 'Stato commessa',
      job_assigned: 'Commessa assegnata',
      job_unassigned: 'Assegnazione rimossa',
      document_type_created: 'Cartella creata',
      document_type_updated: 'Cartella modificata',
      document_type_deleted: 'Cartella eliminata',
      document_uploaded: 'Documento caricato',
      vehicle_created: 'Veicolo aggiunto',
      vehicle_updated: 'Veicolo modificato',
      vehicle_assigned: 'Veicolo assegnato',
      vehicle_returned: 'Veicolo restituito',
      vehicle_deleted: 'Veicolo eliminato',
      document_exempted: 'Documento segnato come non richiesto',
      document_exemption_removed: 'Documento di nuovo obbligatorio',
      document_sent: 'Documento inviato via email',
      document_bulk_renamed: 'File rinominati in automatico',
      document_viewed: 'Documento visualizzato',
      document_downloaded: 'Documento scaricato',
      document_deleted: 'Documento eliminato',
      site_created: 'Cantiere creato',
      site_updated: 'Cantiere modificato',
      settings_changed: 'Impostazioni modificate',
      assignment_created: 'Assegnazione creata',
      assignment_removed: 'Assegnazione rimossa',
      alert_acknowledged: 'Segnalazione presa in carico',
      document_recorded: 'Documento registrato',
      calendar_event_created: 'Appuntamento creato',
      calendar_event_updated: 'Appuntamento modificato',
      calendar_event_deleted: 'Appuntamento eliminato',
      leave_requested: 'Richiesta di ferie o permesso',
      leave_cancelled: 'Richiesta annullata',
      leave_approved: 'Richiesta approvata',
      leave_rejected: 'Richiesta rifiutata',
    },
  },

  inventory: {
    descriptionPlaceholder: 'Che cos\u2019\u00e8 e cosa vale la pena sapere su questo esemplare.',
    purchasePriceHint: 'Lo vede solo l\u2019ufficio.',
    moreDetails: 'Altri dettagli',
    moreDetailsHint: 'Categoria, marca, acquisto',
    delete: 'Elimina articolo',
    deleteTitle: 'Eliminare questo articolo?',
    deleteConfirm:
      '{code} \u2014 {name} va nel cestino e pu\u00f2 essere ripristinato per 30 giorni.',
    deleteOutWarning:
      'Questo articolo \u00e8 in carico a {holder}. Registra prima il rientro: eliminandolo ora si perde l\u2019unica traccia di chi ce l\u2019ha.',
    statusHint_available: 'In magazzino, pronto da consegnare.',
    statusHint_assigned: 'In carico a qualcuno. Si imposta consegnandolo, non a mano.',
    statusHint_maintenance: 'Rientrato ma non utilizzabile: in riparazione, in taratura, in attesa di un pezzo. Resta a registro e fuori dalla lista di consegna.',
    statusHint_lost: 'Non torna indietro, per quanto ne sappiamo.',
    statusHint_retired: 'Rottamato o venduto. Tenuto per lo storico, non per l\u2019uso.',
    statusMeaning: 'Che cosa significano gli stati',
    scan: 'Scansiona',
    scanTitle: 'Scansiona un\u2019etichetta',
    scanHint: 'Inquadra il QR o il codice a barre sull\u2019attrezzo.',
    scanStart: 'Avvia la fotocamera',
    scanStop: 'Ferma',
    scanAgain: 'Scansiona un altro',
    scanNoCamera: 'Nessuna fotocamera disponibile',
    scanNoCameraHint:
      'Questo dispositivo non ha una fotocamera, oppure il browser non ne consente l\u2019uso. Puoi comunque digitare il codice.',
    scanDenied: 'Fotocamera bloccata',
    scanDeniedHint:
      'Consenti l\u2019accesso alla fotocamera per questo sito nelle impostazioni del browser, poi riprova.',
    scanInsecure: 'La fotocamera richiede una connessione sicura',
    scanInsecureHint:
      'I browser danno accesso alla fotocamera solo via HTTPS. Funzioner\u00e0 una volta pubblicato il portale; su questa macchina funziona su localhost.',
    scanManual: 'Oppure digita il codice',
    scanManualAction: 'Cerca',
    scanLooking: 'Ricerca in corso\u2026',
    scanFound: 'Trovato',
    handOver: 'Consegna',
    handBack: 'Ritira',
    scanReady: 'In magazzino',
    scanReadyHint: 'Non ce l\u2019ha nessuno. Consegnalo qui sotto.',
    scanOut: 'In carico a {holder}',
    scanOutHint: 'Dal {date}. Ritiralo qui sotto.',
    installHint:
      'Aggiungi il portale alla schermata home: lo scanner si apre con un tocco, senza barra degli indirizzi.',
    category_power_tools: 'Utensili elettrici',
    category_measuring: 'Strumenti di misura',
    category_ppe: 'DPI',
    category_access: 'Scale e trabattelli',
    category_vehicles: 'Veicoli',
    category_other: 'Altro',
    allCategories: 'Tutte le categorie',
    allStatuses: 'Tutti gli stati',
    photoOf: 'Foto di {name}',
    noPhoto: 'Nessuna foto',
    selectRow: 'Seleziona {name}',
    selectAll: 'Seleziona tutto quello mostrato',
    selectedOne: '{count} selezionato',
    selectedMany: '{count} selezionati',
    clearSelection: 'Annulla selezione',
    printSelected: 'Stampa {count} etichette',
    printAll: 'Stampa tutte le etichette',
    printSheetSelected: 'Etichette per i {count} articoli selezionati.',
    printSheetAll: 'Etichette per tutti gli articoli in inventario.',
    printSaveHint:
      'La finestra di stampa del browser può salvare il foglio in PDF invece di stamparlo: scegli “Salva come PDF” come destinazione.',
    sectionItem: 'Che cos’è',
    sectionItemHint: 'Il nome è quello che poi si cerca.',
    sectionPurchase: 'Acquisto',
    sectionPurchaseHint: 'Facoltativo, e visibile solo all’ufficio.',
    sectionLabel: 'Etichetta',
    sectionPhoto: 'Foto',
    removePhoto: 'Rimuovi foto',
    resolving: 'Ricerca articolo…',
    scanFailed: 'Articolo non trovato',
    scanFailedHint: 'Nessun articolo corrisponde al codice “{code}”.',
    title: 'Inventario',
    subtitle: 'Attrezzatura e strumenti, e chi li ha in questo momento.',
    searchPlaceholder: 'Codice, nome, matricola, chi lo ha\u2026',
    countOne: '{count} articolo',
    countMany: '{count} articoli',
    addItem: 'Aggiungi articolo',
    addItemHint:
      'Il codice viene generato automaticamente e va sull\u2019etichetta. La matricola del costruttore \u00e8 un campo a parte.',
    item: 'Articolo',
    code: 'Codice',
    category: 'Categoria',
    serialNumber: 'Matricola',
    serialHint: 'Quella del costruttore \u2014 \u00e8 il numero che serve a un\u2019assicurazione o a una denuncia.',
    manufacturer: 'Costruttore',
    model: 'Modello',
    purchasedOn: 'Acquistato il',
    purchasePrice: 'Prezzo d\u2019acquisto',
    description: 'Descrizione',
    namePlaceholder: 'Es. Trapano Hilti TE 30',
    photo: 'Foto',
    photoHint: 'Utile per riconoscerlo alla riconsegna.',
    photoLabel: 'Foto inventario',

    holder: 'In carico a',
    holderPerson: 'Dipendente',
    holderSite: 'Cantiere',
    inStore: 'In magazzino',
    dueBack: 'Rientro previsto',
    dueBackHint: 'Lascia vuoto se non c\u2019\u00e8 una data di rientro.',
    checkedOutAt: 'Consegnato il',
    overdue: 'In ritardo',
    overdueBy: '{count} gg di ritardo',
    overdueBanner:
      '{count} articoli non sono rientrati entro la data prevista. Sono in cima all\u2019elenco.',

    status_available: 'Disponibile',
    status_assigned: 'Assegnato',
    status_maintenance: 'In manutenzione',
    status_lost: 'Smarrito',
    status_retired: 'Dismesso',

    checkOut: 'Consegna',
    checkOutTitle: 'Consegna \u201c{name}\u201d',
    checkOutHint: 'Resta assegnato finch\u00e9 non registri il rientro.',
    checkIn: 'Registra rientro',
    checkInTitle: 'Rientro di \u201c{name}\u201d',
    giveTo: 'A chi',
    giveToHint: 'Un dipendente, oppure un cantiere se l\u2019attrezzo resta sul posto.',
    conditionOut: 'Note di consegna',
    conditionHint: 'Condizioni, accessori inclusi, cosa deve tornare indietro.',
    conditionIn: 'Note di rientro',
    backAs: 'Stato al rientro',
    backAsHint: 'Segna \u201cIn manutenzione\u201d se \u00e8 tornato da riparare.',

    history: 'Movimenti',
    historyHint:
      'Ogni consegna e ogni rientro. Le righe non si modificano: sono la risposta a \u201cchi lo aveva a marzo\u201d.',
    neverIssued: 'Questo articolo non \u00e8 mai stato consegnato.',
    stillOut: 'Ancora fuori',
    outOn: 'Uscito il {date}',
    backOn: 'rientrato il {date}',
    backToList: 'Torna all\u2019inventario',

    label: 'Etichetta',
    labelHint:
      'Da stampare e attaccare sull\u2019attrezzo. Inquadrando il codice si apre direttamente questa scheda.',
    labelKind: 'Tipo di etichetta',
    labelKindHint: 'Scegli il codice a barre se l\u2019attrezzo ne ha gi\u00e0 uno.',
    qr: 'QR code',
    barcode: 'Codice a barre',
    externalCode: 'Codice esistente',
    externalCodeHint: 'Se l\u2019attrezzo ha gi\u00e0 un\u2019etichetta del costruttore, scansionarla former\u00e0 comunque questo articolo.',
    print: 'Stampa',
    printLabels: 'Stampa etichette',
    printLabelsHint: 'Un foglio con tutte le etichette, da ritagliare e attaccare.',
  },

  recycle: {
    inventory: 'Articolo',
    title: 'Cestino',
    subtitle:
      'Tutto quello che è stato eliminato. Resta recuperabile per {count} giorni, poi viene rimosso definitivamente.',
    subtitleShort: 'Elementi eliminati, recuperabili per un periodo limitato.',
    searchPlaceholder: 'Nome, tipo, chi ha eliminato…',
    item: 'Elemento',
    kind: 'Tipo',
    client: 'Cliente',
    document: 'Documento',
    deletedAt: 'Eliminato',
    daysLeft: 'Giorni rimasti',
    daysLeftValue: '{count} gg',
    restore: 'Ripristina',
    purge: 'Elimina definitivamente',
    purgeTitle: 'Eliminare definitivamente?',
    purgeConfirm:
      '“{name}” verrà rimosso subito e non potrà più essere recuperato.',
    countOne: '{count} elemento',
    countMany: '{count} elementi',
  },

  settings: {
    courses: {
      title: 'Corsi e patenti',
      hide: 'Nascondi ai dipendenti i documenti di corsi e patenti',
      hideHint:
        "I dipendenti non possono aprire né scaricare dall'app i documenti delle cartelle corsi e patenti. L'ufficio li vede sempre.",
    },
    calLeave: {
      title: 'Calendario e ferie',
      hint: "Cosa vedono e cosa possono fare i dipendenti dall'app.",
      calendarInApp: "Mostra il calendario nell'app",
      calendarInAppHint: "Appuntamenti, scadenze e assenze compaiono nell'app dei dipendenti.",
      employeesCanAdd: 'I dipendenti possono aggiungere appuntamenti',
      employeesCanAddHint: "Un appuntamento aggiunto da un dipendente lo vedono solo lui e l'ufficio.",
      leaveEnabled: 'Richieste di ferie e permessi attive',
      leaveEnabledHint: "I dipendenti possono chiedere ferie e permessi dall'app. Le richieste già inviate restano visibili.",
      managersCanApprove: 'I responsabili possono approvare',
      managersCanApproveHint: "Se è spento, le richieste le approva o rifiuta solo l'amministratore.",
      typesTitle: 'Tipi di richiesta',
      typesHint: 'Cosa possono chiedere i dipendenti. Il nome si può cambiare in ogni momento; il codice interno resta quello della creazione.',
      typeIt: 'Nome in italiano',
      typeEn: 'Nome in inglese',
      typeHours: 'A ore',
      typeHoursHint: 'Un solo giorno, con ora di inizio e di fine.',
      typeAdd: 'Aggiungi un tipo',
      typeRemove: 'Rimuovi il tipo',
      typeKeepOne: 'Deve restare almeno un tipo.',
      typeNameRequired: 'Ogni tipo ha bisogno di un nome in italiano.',
      typeRemovedNote: "Le richieste già inviate con un tipo rimosso restano nell'elenco, con il codice al posto del nome.",
      newType: 'Nuovo tipo',
    },
    auditNote: 'Ogni modifica fatta da chiunque viene registrata e non può essere né modificata né cancellata. Gli amministratori la consultano qui:',
    allowEarly: 'Consenti la timbratura di entrata prima dell\u2019orario previsto',
    allowEarlyHint:
      'Consigliato. Arrivare prima e prepararsi \u00e8 normale in cantiere; la timbratura anticipata viene comunque segnalata all\u2019ufficio.',
    allowLate: 'Consenti la timbratura di uscita dopo l\u2019orario previsto',
    allowLateHint:
      'Disattivandolo, chi finisce tardi non riesce a timbrare l\u2019uscita e il turno viene chiuso automaticamente a fine giornata \u2014 peggio di una segnalazione. \u00c8 questa la voce che gonfia una fattura, quindi vale la pena leggere le segnalazioni invece di bloccare.',
    graceLabel: 'Tolleranza sull\u2019orario (minuti)',
    graceHint:
      'Entro questi minuti la timbratura \u00e8 considerata puntuale. Senza tolleranza, chi timbra alle 08:57 per le 09:00 finisce segnalato ogni mattina.',
    workingHours: 'Orari di lavoro',
    perJobNote:
      'Ogni commessa pu\u00f2 avere regole proprie: le trovi nella scheda della commessa.',
    theme: 'Tema',
    language: 'Lingua',
    perSiteNote:
      'Questi valori valgono per tutti i cantieri. Un singolo cantiere può avere tolleranze proprie: le trovi nella scheda del cantiere, sotto la mappa.',
    openSites: 'Vai ai cantieri',
    location: 'Posizione',
    companyTitle: 'Dati azienda sul tesserino',
    companyHint: 'Stampati in alto su ogni tesserino di riconoscimento. Se cambiano, correggili qui.',
    companyName: 'Ragione sociale',
    companyVat: 'C.F. / Partita IVA',
    companyAddress: 'Sede legale',
    companyEmployer: 'Datore di lavoro (legale rappresentante)',
    saveSettings: 'Salva impostazioni',
    radiusLabel: 'Raggio area predefinito (m)',
    radiusHint: 'Usato per i nuovi cantieri. Ogni cantiere può avere il proprio.',
    accuracyTrustLabel: 'Precisione affidabile (m)',
    accuracyTrustHint: 'Sotto questo valore il rilevamento GPS è considerato attendibile.',
    accuracyRejectLabel: 'Precisione inutilizzabile (m)',
    accuracyRejectHint:
      'Sopra questo valore il rilevamento vale come “GPS non disponibile”, non come “fuori area”.',
    maxShiftLabel: 'Chiusura automatica dopo (ore)',
    maxShiftHint: 'Un turno aperto oltre questo limite viene chiuso e segnalato per verifica.',
    offlineAgeLabel: 'Età massima timbrature offline (ore)',
    offlineAgeHint:
      'Oltre questo limite la timbratura richiede verifica manuale. Non viene mai persa.',

    locationNote:
      'La posizione viene rilevata solo al momento della timbratura di entrata e di uscita, mai durante il turno.',
    title: 'Impostazioni',
    subtitle: 'Il tuo account, le regole aziendali e chi può accedere al portale',
    appearance: 'Aspetto',
    appearanceHint: 'Vale solo per questo browser.',
    clockIn: 'Timbratura',
    allowOutside: 'Consenti la timbratura fuori area (con motivo obbligatorio)',
    allowOutsideHint:
      'Consigliato. Disattivandolo, un dipendente che non riesce a timbrare non può iniziare a lavorare — e l’ufficio finisce per correggere i turni a mano, che è peggio per tutti di una segnalazione onesta.',
    saved: 'Impostazioni salvate.',
  },

  alertText: {
    withHolder: '{holder} — {label}',
    expired: '{subject} scaduto',
    expiring: '{subject} in scadenza',
    expiresToday: '{subject} scade oggi',
    missing: '{subject} mancante',
    missingBody: 'Documento obbligatorio non presente in archivio.',
    lastDay: 'Ultimo giorno di validità.',
    expiredYesterday: 'È scaduto ieri. Va rinnovato prima del prossimo impiego in cantiere.',
    expiredDaysAgo:
      'È scaduto {count} giorni fa. Va rinnovato prima del prossimo impiego in cantiere.',
    expiresTomorrow: 'Scade domani.',
    expiresInDays: 'Scade tra {count} giorni. Conviene avviare il rinnovo adesso.',
  },

  status: {
    job: {
      draft: 'Bozza',
      scheduled: 'Programmato',
      in_progress: 'In corso',
      on_hold: 'Sospeso',
      completed: 'Completato',
      cancelled: 'Annullato',
    },
    shift: {
      open: 'In corso',
      closed: 'Chiuso',
      auto_closed: 'Chiuso automaticamente',
      disputed: 'Contestato',
    },
    user: {
      invited: 'Invitato',
      active: 'Attivo',
      suspended: 'Sospeso',
      archived: 'Archiviato',
    },
    role: {
      admin: 'Amministratore',
      manager: 'Dipendente',
      engineer: 'Dipendente',
    },
    register: {
      valid: 'Valido',
      expiring: 'In scadenza',
      expired: 'Scaduto',
      missing: 'Mancante',
      not_provided: 'Non fornito',
      exempt: 'Non richiesto',
    },
    severity: {
      critical: 'Critico',
      warning: 'Attenzione',
      info: 'Informativo',
    },
    docCategory: {
      contract: 'Contratto',
      drawing: 'Disegno',
      certificate: 'Certificato',
      risk_assessment: 'Valutazione rischi',
      datasheet: 'Scheda tecnica',
      site_photo: 'Foto cantiere',
      signed_sheet: 'Foglio firmato',
      signature: 'Firma',
      id_document: 'Documento identità',
      other: 'Altro',
    },
    flag: {
      outside_geofence: 'Fuori area',
      mocked_location: 'Posizione simulata',
      poor_accuracy: 'Precisione bassa',
      no_fix: 'GPS non disponibile',
      offline_sync: 'Inviato offline',
      auto_closed: 'Chiuso automaticamente',
      manually_adjusted: 'Corretto a mano',
      no_job_assigned: 'Senza commessa',
      overlapping_shift: 'Turni sovrapposti',
    },
    alertKind: {
      document_expiring: 'Documento in scadenza',
      document_expired: 'Documento scaduto',
      document_missing: 'Documento mancante',
      certification_expiring: 'Certificazione in scadenza',
      certification_expired: 'Certificazione scaduta',
      shift_exception: 'Anomalia turno',
      unassigned_worker: 'Dipendente non assegnato',
    },
  },

  /**
   * Sign-in.
   *
   * Written for someone standing in an office who has just been locked out, so every
   * message says what to do next rather than what went wrong internally. Cognito's own
   * error text is never shown: it leaks whether an address exists.
   */
  auth: {
    title: 'Accedi',
    email: 'Email',
    password: 'Password',
    signIn: 'Accedi',
    signingIn: 'Accesso in corso…',
    signOut: 'Esci',
    forgot: 'Password dimenticata?',
    back: 'Torna indietro',
    showPassword: 'Mostra password',
    hidePassword: 'Nascondi password',

    mfa: {
      title: 'Verifica in due passaggi',
      subtitle: 'Inserisci il codice a 6 cifre dalla tua app di autenticazione.',
      subtitleEmail: 'Abbiamo inviato un codice a 6 cifre alla tua email.',
      code: 'Codice di verifica',
      verify: 'Verifica',
      resend: 'Invia di nuovo',
      resent: 'Codice inviato di nuovo.',
    },

    setupMfa: {
      title: 'Attiva la verifica in due passaggi',
      subtitle:
        'Scansiona questo codice con Google Authenticator, Microsoft Authenticator o 1Password, poi inserisci il codice a 6 cifre che appare.',
      manual: 'Oppure inserisci questa chiave a mano:',
      copied: 'Chiave copiata.',
      confirm: 'Attiva',
      required: 'Il tuo ruolo richiede la verifica in due passaggi.',
      skip: 'Salta per ora',
    },

    newPassword: {
      title: 'Scegli una nuova password',
      subtitle: 'È il tuo primo accesso. La password temporanea non è più valida dopo questo passaggio.',
      password: 'Nuova password',
      confirm: 'Conferma password',
      submit: 'Salva e accedi',
      mismatch: 'Le due password non coincidono.',
      rules: 'Almeno 12 caratteri, con una maiuscola, una minuscola e un numero.',
    },

    forgotFlow: {
      invitationSent: 'Non hai ancora impostato una password: ti abbiamo inviato un nuovo invito con una password temporanea. Controlla la posta.',
      title: 'Reimposta la password',
      subtitle: 'Inserisci la tua email: se esiste un account, riceverai un codice a 6 cifre.',
      send: 'Invia codice',
      sent: 'Se l’indirizzo è registrato, il codice è in arrivo.',
      codeTitle: 'Inserisci il codice',
      codeSubtitle: 'Controlla la tua email e scegli una nuova password.',
      code: 'Codice',
      newPassword: 'Nuova password',
      confirm: 'Conferma password',
      submit: 'Reimposta password',
      done: 'Password aggiornata. Ora puoi accedere.',
    },

    remember: {
      label: 'Ricordami su questo dispositivo',
      welcomeBack: 'Bentornato',
      notYou: 'Non sei tu? Usa un altro account',
      orPassword: 'oppure con la password',
    },
    passkey: {
      signIn: 'Accedi con una passkey',
      or: 'oppure',
      needEmail: 'Inserisci prima la tua email, poi usa la passkey.',
    },

    secure: {
      title: 'Proteggi il tuo account',
      subtitle: 'Aggiungi un secondo livello di sicurezza. Puoi farlo anche più tardi da Impostazioni → Account.',
      passkey: 'Aggiungi una passkey',
      passkeyHint: 'Accedi con l’impronta, il volto o il PIN del dispositivo. Niente codici da digitare.',
      recommended: 'Consigliato',
      app: 'App di autenticazione',
      appHint: 'Un codice a 6 cifre da Google Authenticator, Microsoft Authenticator o 1Password.',
      skip: 'Salta per ora',
    },

    session: {
      expiring: 'La sessione sta per scadere',
      expiringBody: 'Verrai disconnesso tra {seconds} secondi per inattività.',
      stay: 'Resta connesso',
      timedOut: 'Sessione scaduta per inattività. Accedi di nuovo.',
      maxReached: 'Sessione scaduta. Per sicurezza l’accesso dura al massimo 5 ore.',
      noPortalAccess: 'Questo account non ha accesso al portale interno. Chiedi a un amministratore di attivarlo.',
    },

    error: {
      invalid: 'Email o password non corretti.',
      code: 'Codice non valido o scaduto.',
      attempts: 'Troppi tentativi. Riprova tra qualche minuto.',
      disabled: 'Questo account non è attivo. Contatta l’ufficio.',
      password: 'La password non rispetta i requisiti minimi.',
      network: 'Impossibile contattare il server. Controlla la connessione.',
      unknown: 'Qualcosa è andato storto. Riprova.',
      noPasskey: 'Accesso con passkey non disponibile per questo account: nessuna passkey registrata, oppure è attiva l’app di autenticazione (le due cose non funzionano insieme). Accedi con la password.',
      passkeyCancelled: 'Passkey annullata o non trovata su questo dispositivo.',
      passkeyUnsupported: 'Questo browser o dispositivo non supporta le passkey per questo portale.',
      passkeyExists: 'Questo dispositivo ha già una passkey per il tuo account.',
      notConfigured:
        'Autenticazione non configurata per questa build. Contatta chi gestisce il sistema.',
    },
  },

  /** Settings → Users: who may open what. */
  access: {
    noEmail: 'Senza email — nessun account',
    noEmailHint:
      "Senza email la persona viene aggiunta senza account: puoi subito archiviare i suoi documenti. Dopo averla aggiunta potrai creare l'accesso all'app con nome e cognome e una password temporanea, oppure aggiungere l'email più tardi dalla sua pagina.",
    noAccountYet:
      'Nessun account ancora. Aggiungi l’email e scegli dove potrà accedere: riceverà l’invito via email.',
    createAccount: 'Crea account',
    createAccountHint: 'L’invito viene inviato a questo indirizzo.',
    invited: 'Invitato',
    noSignIn: 'Senza accesso',
    delete: 'Elimina',
    deleting: 'Eliminazione…',
    deleteConfirm: 'Eliminare {name} ({email})?\n\nNon potrà più accedere e sparirà dagli elenchi. I suoi turni, documenti e il registro attività restano: sono dati che l’azienda deve conservare.',
    reinvite: 'Invia di nuovo l’invito',
    reinviting: 'Invio…',
    reinviteDone: 'Invito inviato',
    reinviteSent: 'Nuova password temporanea inviata a {email}.',
    reinviteHint: 'Invia una nuova password temporanea: quella del primo invito scade dopo 7 giorni.',
    nameLogin: {
      heading: "Accesso all'app con nome",
      loginName: 'Nome di accesso',
      none: "Non ha ancora un accesso con nome. Crealo per far entrare la persona nell'app con nome, cognome e una password temporanea.",
      typedHint: "Nell'app scrive nome \"{first}\", cognome \"{last}\" e la sua password.",
      differentName:
        'Questo nome di accesso non coincide con nome e cognome: deve scrivere esattamente "{loginName}".',
      create: 'Crea accesso con nome',
      reset: 'Reimposta password',
      createTitle: 'Accesso con nome per {name}',
      resetTitle: 'Reimposta la password di {name}',
      resultTitle: 'Password temporanea per {name}',
      createIntro:
        "Crea l'accesso all'app con nome e cognome e una password temporanea. Al primo accesso dovrà scegliere una password sua.",
      resetIntro:
        'Genera una nuova password temporanea. Quella di prima smette di funzionare subito e al prossimo accesso dovrà scegliere una password sua.',
      leavesEmail:
        "Oggi accede con l'email {email}. Con l'accesso con nome entrerà con nome e cognome e la password di prima non varrà più.",
      offerAfterAdd:
        "{name} è stato aggiunto senza email. Vuoi creare subito l'accesso all'app con nome e cognome? Avrai una password temporanea da consegnargli.",
      notNow: 'Non ora',
      generate: 'Genera password',
      needName:
        'Non riesco a ricavare un nome di accesso dal nome di questa persona. Scrivine uno: lettere minuscole, numeri e punti (per esempio mario.rossi).',
      nameInvalid: 'Usa solo lettere minuscole senza accenti, numeri e punti, per esempio mario.rossi.',
      password: 'Password temporanea',
      copy: 'Copia',
      copied: 'Copiata',
      copyFailed: 'Copia non riuscita: seleziona la password e copiala a mano.',
      onceWarning:
        'Questa password si vede solo ora: non viene salvata e non si può rivedere. Al primo accesso la persona dovrà scegliere una password sua. Se la perdi, reimpostala.',
      instructionLabel: 'Istruzioni da consegnare',
      instruction:
        "Apri l'app HH Electrical Systems. Accedi con: {first} {last}, password: {password}.",
      instructionLogin:
        "Apri l'app HH Electrical Systems. Accedi con il nome di accesso: {loginName}, password: {password}.",
      copyInstruction: 'Copia istruzioni',
      done: 'Fatto',
    },
    heading: 'Accesso e permessi',
    signsInTo: 'Può accedere a',
    manage: 'Gestisci accesso',
    discard: 'Annulla modifiche',
    portalAccess: 'Cosa può fare nel portale',
    permissionsForNew: 'Cosa potrà fare nel portale',
    summary: '{visible} sezioni visibili · {editable} modificabili',
    fullAccess: 'Gli amministratori hanno accesso completo a tutte le sezioni.',
    appOnly: 'Solo app: nessun accesso al portale. Attiva “Portale interno” qui sopra per scegliere cosa può vedere.',
    setAll: 'Tutte:',
    presets: {
      title: 'Punto di partenza:',
      viewAll: 'Vede tutto',
      editDaily: 'Gestisce il lavoro quotidiano',
      nothing: 'Nessun accesso',
    },
    groups: {
      overview: 'Panoramica',
      operations: 'Operatività',
      records: 'Anagrafiche',
      compliance: 'Conformità e messaggi',
      system: 'Sistema',
    },
    tabGeneral: 'Generale',
    tabUsers: 'Utenti e permessi',
    title: 'Utenti del portale',
    hint: 'Ogni persona ha un ruolo, che decide i permessi di partenza. Qui puoi cambiare sezione per sezione cosa può vedere o modificare.',
    newUser: 'Nuovo utente',
    newUserHint:
      'Con l’email riceverà un invito con una password temporanea. Senza email viene solo registrato: l’account lo crei più tardi.',
    firstName: 'Nome',
    lastName: 'Cognome',
    email: 'Email',
    role: 'Ruolo',
    create: 'Crea utente',
    phone: 'Telefono',
    kind: 'Chi è',
    kindAdmin: 'Amministratore',
    kindAdminHint: 'Gestisce tutto, compresi utenti e permessi. Ha sempre il portale.',
    kindEmployee: 'Dipendente',
    kindEmployeeHint: 'Tecnico o personale d’ufficio. Cosa può vedere lo decidi nei permessi.',
    doors: 'Accessi da creare',
    doorPortal: 'Portale interno',
    doorPortalHint: 'Accede da computer: turni, cantieri, documenti, in base ai permessi.',
    doorApp: 'App',
    doorAppHint: 'Timbra, vede le sue commesse e i suoi documenti dal telefono.',
    doorPortalShort: 'Portale',
    doorAppShort: 'App',
    noDoorsHint: 'Nessun accesso: la persona viene registrata ma non potrà accedere finché non attivi un accesso da qui.',
    created: 'Utente creato. Riceverà l’email con la password temporanea.',
    permissions: 'Permessi',
    section: 'Sezione',
    levelShort: {
      none: 'Nessuno',
      read: 'Vedere',
      write: 'Modificare',
    },
    level: {
      none: 'Nessun accesso',
      read: 'Solo lettura',
      write: 'Lettura e modifica',
    },
    inherited: 'dal ruolo',
    save: 'Salva permessi',
    saved: 'Permessi salvati.',
    suspend: 'Sospendi',
    reactivate: 'Riattiva',
    you: 'tu',
    selfLock: 'Non puoi togliere a te stesso la gestione degli utenti.',
    empty: 'Nessun utente. Crea il primo.',
    sections: {
      dashboard: 'Dashboard',
      shifts: 'Turni',
      payroll: 'Ore e paga',
      assignments: 'Assegnazioni',
      jobs: 'Commesse',
      employees: 'Dipendenti',
      sites: 'Cantieri',
      documents: 'Documenti',
      compliance: 'Scadenze e documenti obbligatori',
      alerts: 'Avvisi',
      inventory: 'Inventario',
      vehicles: 'Veicoli',
      audit: 'Registro attività',
      recycle_bin: 'Cestino',
      settings: 'Impostazioni',
      users: 'Utenti e permessi',
      messages: 'Messaggi',
      calendar: 'Calendario',
      leave: 'Ferie e permessi',
    },
  },

  messages: {
    search: 'Cerca per nome, email o testo',
    noMatches: 'Nessun messaggio corrisponde alla ricerca.',
    selectOne: 'Seleziona un messaggio per leggerlo.',
    reply: 'Rispondi',
    call: 'Chiama',
    back: 'Tutti i messaggi',
    email: 'Email',
    language: 'Lingua',
    readLabel: 'Letto',
    title: 'Messaggi',
    subtitle: 'Richieste arrivate dal modulo di contatto del sito',
    inbox: 'In arrivo',
    archived: 'Archiviati',
    new: 'Nuovo',
    noSubject: 'Senza oggetto',
    empty: 'Nessun messaggio.',
    emptyHint: 'Quando qualcuno compila il modulo sul sito, la richiesta compare qui.',
    emptyArchived: 'Nessun messaggio archiviato.',
    readBy: 'Letto da {name} il {when}',
    wroteInItalian: 'Ha scritto in italiano',
    wroteInArabic: 'Ha scritto in arabo',
    wroteInEnglish: 'Ha scritto in inglese',
    archive: 'Archivia',
    restore: 'Ripristina',
  },
  /** Settings → Account: per-person preferences and sign-in security. */
  nudge: {
    pushTitle: 'Attiva le notifiche',
    pushText: 'Promemoria dei lavori, scadenze e novità su questo dispositivo, anche con l’app chiusa.',
    pushOn: 'Attiva',
    dismiss: 'Non ora',
  },
  notifications: {
    tab: 'Notifiche',
    bell: 'Notifiche',
    unread: '{count} da leggere',
    markAllRead: 'Segna tutte come lette',
    empty: 'Nessuna notifica.',
    emptyHint: 'Promemoria, scadenze e novità arriveranno qui.',
    settingsLink: 'Impostazioni notifiche',
    intro: 'Scegli cosa ricevere, come e con quanto anticipo. Vale per il portale e per l’app.',
    groups: {
      schedule: 'Il tuo programma',
      documents: 'I tuoi documenti',
      equipment: 'Attrezzatura in carico',
      office: 'Per l’ufficio',
    },
    types: {
      job_start: { title: 'Inizio lavoro', hint: 'Un promemoria prima dell’inizio di ogni lavoro a cui sei assegnato.' },
      clock_out: { title: 'Ricorda di timbrare l’uscita', hint: 'Se sei ancora in servizio dopo la fine prevista del lavoro.' },
      job_assigned: { title: 'Nuovo lavoro assegnato', hint: 'Quando l’ufficio ti assegna a un lavoro.' },
      job_changed: { title: 'Lavoro spostato o annullato', hint: 'Quando cambia l’orario di un tuo lavoro, o viene annullato.' },
      shift_adjusted: { title: 'Turno corretto dall’ufficio', hint: 'Quando l’ufficio modifica gli orari di una tua timbratura, con il motivo.' },
      document_expiring: { title: 'Documento in scadenza', hint: 'Certificati, corsi e documenti personali, prima della scadenza.' },
      document_expired: { title: 'Documento scaduto', hint: 'Dopo la scadenza, se non è ancora stato rinnovato.' },
      equipment_due: { title: 'Attrezzatura da restituire', hint: 'Prima della data di restituzione di un’attrezzatura che hai in carico.' },
      equipment_overdue: { title: 'Attrezzatura in ritardo', hint: 'Quando la data di restituzione è passata.' },
      staff_document_expiring: { title: 'Documenti del personale in scadenza', hint: 'I documenti di tutti i dipendenti, prima della scadenza.' },
      missed_clock_in: { title: 'Timbratura mancante', hint: 'Quando qualcuno assegnato a un lavoro non ha timbrato dopo l’inizio.' },
      shift_flagged: { title: 'Timbratura irregolare', hint: 'Timbrature fuori area o fuori orario, appena avvengono.' },
      new_enquiry: { title: 'Nuova richiesta dal sito', hint: 'Quando qualcuno compila il modulo di contatto.' },
      staff_equipment_overdue: { title: 'Attrezzature non restituite', hint: 'Un riepilogo al mattino, se ci sono attrezzature in ritardo.' },
      event_invited: { title: 'Invito a un appuntamento', hint: "Quando l'ufficio ti aggiunge a un appuntamento." },
      leave_decided: { title: 'Risposta a ferie o permesso', hint: 'Quando la tua richiesta viene approvata o rifiutata.' },
      leave_requested: { title: 'Nuova richiesta di ferie o permesso', hint: 'Quando un dipendente chiede ferie o un permesso.' },
    },
    timing: {
      job_start: 'Con quanto anticipo',
      clock_out: 'Quanto dopo la fine prevista',
      document_expiring: 'Quanto prima della scadenza',
      equipment_due: 'Quanto prima della restituzione',
      staff_document_expiring: 'Quanto prima della scadenza',
      missed_clock_in: 'Quanto dopo l’inizio',
    },
    device: {
      title: 'Notifiche push su questo dispositivo',
      on: 'Attive: i promemoria arrivano qui anche con il portale chiuso.',
      off: 'Ricevi i promemoria su questo dispositivo anche con il portale chiuso.',
      denied: 'Le notifiche per questo sito sono bloccate nelle impostazioni del browser. Sbloccale lì, poi torna qui.',
      installFirst: 'Su iPhone e iPad: tocca Condividi → Aggiungi alla schermata Home, apri il portale da lì e attivale.',
      unsupported: 'Questo browser non può ricevere notifiche push.',
      turnOn: 'Attiva',
      turnOff: 'Disattiva',
      failed: 'Attivazione non riuscita. Riprova.',
    },
    multiple: 'Puoi sceglierne più di uno.',
    how: 'Anche con',
    alsoInBell: 'E sempre nella campanella.',
    onlyInBell: 'Solo nella campanella: nessuna email, nessuna notifica push.',
    channels: { in_app: 'Portale e app', email: 'Email', push: 'Notifica push' },
    always: 'Sempre attivo',
    emailPending: 'L’invio delle email è in fase di attivazione: fino ad allora le troverai qui.',
    pushMissing: 'Per le notifiche push attivale su questo dispositivo qui sopra, oppure installa l’app HH Electrical Systems sul telefono.',
    minutes: '{count} min',
    hourOne: '1 ora',
    hours: '{count} ore',
    dayOne: '1 giorno',
    days: '{count} giorni',
    sameDay: 'Il giorno stesso',
    atEnd: 'Alla fine',
    quiet: 'Non disturbare',
    quietHint: 'In questa fascia email e push aspettano; le notifiche restano comunque qui. Ora italiana.',
    quietOn: 'Attiva',
    from: 'Dalle',
    to: 'Alle',
    emailLanguage: 'Lingua delle email',
    emailLanguageHint: 'Qui le notifiche seguono la lingua che hai scelto; le email usano questa.',
    save: 'Salva preferenze',
    saved: 'Preferenze salvate.',
    discard: 'Annulla modifiche',
    on: 'Attiva',
    off: 'Disattivata',
  },
  account: {
    passkeyBlockedByApp: 'Con l’app di autenticazione attiva non puoi accedere con la passkey: il servizio di accesso di Amazon non permette le due cose insieme. La passkey (impronta, volto o PIN) conta già come doppia verifica, quindi puoi disattivare l’app senza perdere sicurezza.',
    turnOffAppForPasskey: 'Disattiva l’app e usa la passkey',
    passkeyReady: 'Fatto: ora puoi accedere con la passkey.',
    appDisablesPasskeys: 'Con l’app di autenticazione attiva non potrai accedere con le tue passkey (solo con password e codice). Continuare?',
    tab: 'Account',
    tabRules: 'Regole di timbratura',
    security: 'Accesso e sicurezza',
    securityHint: 'Come entri nel portale. Nessuna di queste opzioni è obbligatoria, ma una passkey è la più comoda e la più sicura.',
    passkeys: 'Passkey',
    passkeysHint: 'Accedi con impronta, volto o PIN del dispositivo, senza password né codici. Una per ogni dispositivo che usi.',
    addPasskey: 'Aggiungi passkey',
    noPasskeys: 'Nessuna passkey.',
    unnamed: 'Passkey',
    addedOn: 'Aggiunta il {date}',
    remove: 'Rimuovi',
    removeConfirm: 'Rimuovere questa passkey? Su quel dispositivo dovrai di nuovo usare la password.',
    passkeyAdded: 'Passkey aggiunta.',
    unsupported: 'Questo browser non supporta le passkey.',
    app: 'App di autenticazione',
    appHint: 'Dopo la password ti verrà chiesto un codice a 6 cifre. Non richiesto quando entri con una passkey.',
    on: 'Attiva',
    off: 'Non attiva',
    setUp: 'Configura',
    turnOff: 'Disattiva',
    turnOffConfirm: 'Disattivare l’app di autenticazione? Con la sola password l’account è meno protetto.',
    enabled: 'App di autenticazione attivata.',
    disabled: 'App di autenticazione disattivata.',
    cancel: 'Annulla',
    password: 'Password',
    passwordHint: 'Almeno 12 caratteri, con una maiuscola, una minuscola e un numero.',
    currentPassword: 'Password attuale',
    newPassword: 'Nuova password',
    confirmPassword: 'Conferma password',
    changePassword: 'Cambia password',
    passwordChanged: 'Password aggiornata.',
    mismatch: 'Le due password non coincidono.',
  },
} as const;
